import {useState, useCallback, useMemo} from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  InlineStack,
  Icon,
  Box,
  Button,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  ShieldCheckMarkIcon,
  PlusCircleIcon,
  XIcon,
} from "@medentic/polaris-icons";
import {VisibilityIcon} from "@features/events/components/EventBadgeRenderer";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {Event, Instructor} from "@features/events/types";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import AssignInstructorsModal from "./AssignInstructorsModal";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import {getLocalizedText} from "@shared/lib";

interface InstructorItemProps {
  instructor: Instructor;
  onRemove: (instructorId: string) => void;
  isDeleting?: boolean;
}

const InstructorItem: React.FC<InstructorItemProps> = ({
  instructor,
  onRemove,
  isDeleting = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const headlineClampStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${instructor.nameWithPrefix || "instructor"}`}
      dragHandleAccessibilityLabel={`Reorder ${instructor.nameWithPrefix || "instructor"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            <EventsDefaultAvatar
              name={instructor.nameWithPrefix || ""}
              singleImage={instructor.avatar}
            />
            <Box minWidth="0" width="100%">
              <BlockStack gap="100">
                <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
                  <div style={{minWidth: 0}}>
                    <Text
                      as="span"
                      variant="bodyMd"
                      fontWeight={
                        instructor.visibility === 1 ? "semibold" : "regular"
                      }
                      tone={instructor.visibility === 1 ? "base" : "subdued"}
                      truncate
                    >
                      {instructor.nameWithPrefix}
                    </Text>
                  </div>
                  {instructor.verified && (
                    <div style={{flexShrink: 0}}>
                      <Icon source={ShieldCheckMarkIcon} tone="success" />
                    </div>
                  )}
                  <div style={{flexShrink: 0}}>
                    <VisibilityIcon visibility={instructor.visibility} />
                  </div>
                </InlineStack>
                <div style={headlineClampStyle}>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {getLocalizedText(instructor.headline) || instructor.title}
                  </Text>
                </div>
              </BlockStack>
            </Box>
          </InlineStack>

          <InlineStack
            gap="300"
            align="space-between"
            blockAlign="center"
            wrap={false}
          >
            <Box maxWidth="80%">
              <Text as="p" variant="bodyMd" tone="subdued" truncate>
                {getLocalizedText(instructor.geoAddress?.summary)}
              </Text>
            </Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={XIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(instructor.id)}
                  loading={isDeleting}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

interface InstructorsCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

export default function InstructorsCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: InstructorsCardProps) {
  const {t} = useTranslation();
  const {eventOrganizerId: hcc} = useParams<{
    eventOrganizerId: string;
  }>();
  const {GET} = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const assignedInstructorIds = useMemo(
    () => eventData.instructorIds ?? [],
    [eventData.instructorIds],
  );

  const {data: allInstructors = []} = useQuery<Instructor[]>({
    queryKey: ["all-instructors", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/instructors`),
    enabled: Boolean(hcc && assignedInstructorIds.length > 0),
  });

  const assignedInstructors = assignedInstructorIds
    .map((id) => allInstructors.find((instructor) => instructor.id === id))
    .filter(Boolean) as Instructor[];

  const handleAssignInstructors = () => {
    setIsModalOpen(true);
  };

  const handleModalAssign = (instructorIds: string[]) => {
    onDataChange({instructorIds});
  };

  const handleRemoveInstructor = useCallback(
    (instructorId: string) => {
      const updatedIds = assignedInstructorIds.filter((id) => id !== instructorId);
      onDataChange({instructorIds: updatedIds});
    },
    [assignedInstructorIds, onDataChange],
  );

  const hasAssignedInstructors = assignedInstructors.length > 0;

  return (
    <>
      <Card
        background={
          fieldErrors.instructorIds ? "bg-surface-critical" : undefined
        }
      >
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text as="h2" variant="headingMd" requiredIndicator>
              {t("eventDetails.instructors.title")}
            </Text>
          </InlineStack>

          <Text as="p" variant="bodyMd" tone="subdued">
            {t("eventDetails.instructors.description")}
          </Text>

          {hasAssignedInstructors ? (
            <BlockStack gap="300">
              <Box
                borderWidth="025"
                borderColor="border"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={assignedInstructors}
                  onChange={(reorderedInstructors) => {
                    const reorderedIds = reorderedInstructors.map(
                      (instructor) => instructor.id,
                    );
                    onDataChange({instructorIds: reorderedIds});
                  }}
                  idForItem={(instructor) => instructor?.id}
                  renderItem={(item) => (
                    <InstructorItem
                      instructor={item}
                      onRemove={handleRemoveInstructor}
                    />
                  )}
                  showDividers
                />
              </Box>
              <InlineStack>
                <Button
                  onClick={handleAssignInstructors}
                  variant="plain"
                  icon={PlusCircleIcon}
                >
                  {t("eventDetails.instructors.assignButton")}
                </Button>
              </InlineStack>
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("eventDetails.instructors.emptyState")}
              action={{
                content: t("eventDetails.instructors.assignButton"),
                icon: PlusIcon,
                onAction: handleAssignInstructors,
              }}
              image=""
            />
          )}

          {fieldErrors.instructorIds && (
            <Text as="p" variant="bodyMd" tone="critical">
              {fieldErrors.instructorIds}
            </Text>
          )}
        </BlockStack>
      </Card>

      <AssignInstructorsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedInstructorIds={assignedInstructorIds}
        onAssign={handleModalAssign}
      />
    </>
  );
}
